/**
 * Serialize a rendered chat-markdown DOM into clipboard payloads.
 *
 * The Copy response button used to write the markdown source (`**bold**`,
 * `[label](cite:…)`, Streamdown chrome). Operators want the visible answer:
 * plain text of what they see, plus HTML so rich paste keeps links/lists.
 */

const SEMANTIC_BLOCKS = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'TR',
  'PRE',
  'BLOCKQUOTE',
  'TABLE',
  'UL',
  'OL',
  'HR'
])

const STREAMDOWN_CHROME = new Set([
  'code-block-header',
  'code-block-copy-button',
  'code-block-download-button'
])

export interface ClipboardPayload {
  text: string
  html?: string
}

function isCopyChrome(el: Element): boolean {
  const kind = el.getAttribute('data-streamdown')
  if (kind !== null && STREAMDOWN_CHROME.has(kind)) return true
  if (el.tagName === 'BUTTON') return true
  if (el.getAttribute('role') === 'button') return true
  return el.classList.contains('katex-mathml')
}

export function serializeRenderedMarkdown(root: HTMLElement): ClipboardPayload {
  const htmlRoot = root.cloneNode(true) as HTMLElement
  stripCopyChrome(htmlRoot)
  const text = normalizeCopiedText(collectPlainText(root))
  return { text, html: htmlRoot.innerHTML }
}

export async function writeClipboardPayload(payload: ClipboardPayload): Promise<void> {
  const clipboard = navigator.clipboard
  if (payload.html && clipboard?.write && typeof ClipboardItem === 'function') {
    try {
      await clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([payload.text], { type: 'text/plain' }),
          'text/html': new Blob([payload.html], { type: 'text/html' })
        })
      ])
      return
    } catch {
      // Fall through to writeText — some contexts expose write() but reject
      // mixed MIME ClipboardItems.
    }
  }
  if (clipboard?.writeText) {
    await clipboard.writeText(payload.text)
    return
  }
  const textarea = document.createElement('textarea')
  textarea.value = payload.text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'absolute'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

function stripCopyChrome(root: HTMLElement): void {
  for (const el of Array.from(root.querySelectorAll('*'))) {
    if (isCopyChrome(el)) el.remove()
  }
}

function collectPlainText(root: Node): string {
  const parts: string[] = []
  walkPlainText(root, parts)
  return parts.join('')
}

function walkPlainText(node: Node, parts: string[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const value = node.nodeValue ?? ''
    if (isInsidePreformatted(node)) {
      parts.push(value)
      return
    }
    // Layout-only whitespace in the markdown DOM (newlines between
    // <tr>s, indent between Streamdown wrappers) is not visible text.
    if (value.trim() === '') return
    parts.push(value.replace(/\s+/g, ' '))
    return
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return
  const el = node as HTMLElement
  if (isCopyChrome(el)) return

  const tag = el.tagName
  if (tag === 'BR' || tag === 'HR') {
    pushNewline(parts)
    return
  }
  if (tag === 'TD' || tag === 'TH') {
    const prev = el.previousElementSibling
    if (prev !== null && (prev.tagName === 'TD' || prev.tagName === 'TH')) {
      parts.push('\t')
    }
  } else if (SEMANTIC_BLOCKS.has(tag)) {
    pushNewline(parts)
  }

  for (const child of Array.from(el.childNodes)) {
    walkPlainText(child, parts)
  }

  if (
    tag === 'P' ||
    tag === 'LI' ||
    tag === 'TR' ||
    tag === 'PRE' ||
    tag === 'BLOCKQUOTE' ||
    tag === 'H1' ||
    tag === 'H2' ||
    tag === 'H3' ||
    tag === 'H4' ||
    tag === 'H5' ||
    tag === 'H6'
  ) {
    pushNewline(parts)
  }
}

function isInsidePreformatted(node: Node): boolean {
  let current = node.parentElement
  while (current) {
    if (current.tagName === 'PRE' || current.tagName === 'CODE') return true
    current = current.parentElement
  }
  return false
}

function pushNewline(parts: string[]): void {
  if (parts.length === 0) return
  if (!parts[parts.length - 1].endsWith('\n')) parts.push('\n')
}

function normalizeCopiedText(text: string): string {
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
